import { type UpdateFocusAreaInstanceMutation } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { updateFocusAreaInstanceMutationDocument } from "./focus-area-instance.gql";
import { EVENT_FOCUS_AREA_INSTANCE_UPDATED, trackFocusAreaEvent } from "./tracking";

export type UpdatedFocusAreaInstance = UpdateFocusAreaInstanceMutation["updateFocusAreaInstance"];

export interface UseUpdateFocusAreaInstanceProps {
  onCompleted?: (data: UpdatedFocusAreaInstance) => void;
  onError?: (e: Error) => void;
}

export const useUpdateFocusAreaInstance = ({ onCompleted, onError }: UseUpdateFocusAreaInstanceProps = {}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: { id: string; focusAreaId?: string; ownerId?: string; ownerType?: string }) => {
      const client = createOperationClient("updateFocusAreaInstance");
      const data = await client.request(updateFocusAreaInstanceMutationDocument, {
        input: {
          id: input.id,
          focusAreaId: input.focusAreaId || "",
        },
      });
      return data.updateFocusAreaInstance;
    },
    onSuccess: (focusAreaInstance) => {
      // Update the focus area instance in the cache
      queryClient.setQueryData(queryKeys.focusAreas.instance(focusAreaInstance.id), focusAreaInstance);

      // Update the owner-specific cache
      queryClient.setQueryData(queryKeys.focusAreas.instanceByOwner(focusAreaInstance.ownerId), focusAreaInstance);

      // Invalidate the owner's detail query to reflect the change
      if (focusAreaInstance.ownerType === "CLIMATE_ACTION") {
        queryClient.invalidateQueries({
          queryKey: queryKeys.climateActions.detail(focusAreaInstance.ownerId),
        });
      } else if (focusAreaInstance.ownerType === "PROJECT") {
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.detail(focusAreaInstance.ownerId),
        });
      } else if (focusAreaInstance.ownerType === "INDICATOR") {
        queryClient.invalidateQueries({
          queryKey: queryKeys.indicators.detail(focusAreaInstance.ownerId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.indicators.all,
        });
      }

      // Invalidate and refetch focus area lists for this workspace
      queryClient.invalidateQueries({
        queryKey: queryKeys.focusAreas.list(focusAreaInstance.workspaceId),
      });

      // Invalidate climate action list for this workspace since they include focus area instance data
      queryClient.invalidateQueries({
        queryKey: queryKeys.climateActions.list(focusAreaInstance.workspaceId),
      });

      // Invalidate project queries since they include focus area instance data
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.all,
      });

      // Invalidate workspace queries since they include focus area data
      queryClient.invalidateQueries({
        queryKey: queryKeys.workspaces.all,
      });

      // Track the event
      trackFocusAreaEvent(EVENT_FOCUS_AREA_INSTANCE_UPDATED, focusAreaInstance);

      // Call the onCompleted callback
      if (onCompleted) {
        onCompleted(focusAreaInstance);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
  });

  return {
    updateFocusAreaInstance: mutation.mutate,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
